import { formatPackageLegEndpointHe } from "./flightEndpointDisplayHe";
import { formatIsraelDisplayDate } from "./DatesHelpers";

function splitDateTime(full) {
  if (!full || typeof full !== "string") return ["", ""];
  const parts = full.trim().split(/\s+/);
  return [parts[0] || "", parts[1] || ""];
}

/** תאריך יציאה גולמי מרגל LDS (טוקן תאריך ראשון מ־depDate או שדה מקביל). */
function packageLegDepartureDateRaw(leg) {
  if (!leg || typeof leg !== "object") return "";
  const dep = leg.depDate != null ? String(leg.depDate).trim() : "";
  const [dateToken] = splitDateTime(dep);
  if (dateToken) return dateToken;
  if (leg.departureDate != null) {
    const s = String(leg.departureDate).trim();
    if (s) return s.split(/\s+/)[0] || s;
  }
  return "";
}

/**
 * תאריך יציאה לתצוגה (DD/MM/YYYY). ריק אם אין נתון תקף — בלי הערכות.
 * @param {object|null} leg
 * @returns {string}
 */
export function formatPackageLegDepartureDateHe(leg) {
  return formatIsraelDisplayDate(packageLegDepartureDateRaw(leg));
}

const PACKAGE_FLIGHT_SEAT_KEYS = [
  "seats",
  "Seats",
  "availableSeats",
  "AvailableSeats",
  "seatsAvailable",
  "SeatsAvailable",
  "numSeats",
  "NumSeats",
  "freeSeats",
  "FreeSeats",
  "numOfSeats",
  "NumOfSeats",
  "noOfSeats",
  "NoOfSeats",
  "numberOfSeats",
  "NumberOfSeats",
  "seatCount",
  "SeatCount",
  "seatsLeft",
  "SeatsLeft",
  "numOfAvailableSeats",
  "NumOfAvailableSeats",
  "availableSeatCount",
  "AvailableSeatCount",
];

function readPackageSeatScalar(raw) {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (Number.isFinite(n) && n >= 0) return n;
  const s = String(raw).trim();
  return s || null;
}

function readSeatsFromObject(obj) {
  if (!obj || typeof obj !== "object") return null;
  for (const key of PACKAGE_FLIGHT_SEAT_KEYS) {
    const v = readPackageSeatScalar(obj[key]);
    if (v != null) return v;
  }
  return null;
}

/** @param {object|null} legOrWrapper — LDS leg or flight wrapper with `.leg` */
export function resolvePackageLegSeats(legOrWrapper) {
  if (!legOrWrapper || typeof legOrWrapper !== "object") return null;
  const leg = legOrWrapper.leg ?? legOrWrapper;
  const objects = legOrWrapper !== leg ? [legOrWrapper, leg] : [leg];
  for (const obj of objects) {
    const v = readSeatsFromObject(obj);
    if (v != null) return v;
  }
  return null;
}

/** Seats from linkedFlights `flight[i]` entry (wrapper + leg). */
export function resolvePackageFlightEntrySeats(flightEntry) {
  if (!flightEntry || typeof flightEntry !== "object") return null;
  return (
    resolvePackageLegSeats(flightEntry) ??
    resolvePackageLegSeats(flightEntry.leg)
  );
}

/** @returns {string|null} e.g. "9 מקומות" */
export function formatPackageLegSeatsHe(legOrWrapper) {
  const seats = resolvePackageLegSeats(legOrWrapper);
  if (seats == null) return null;
  return `${seats} מקומות`;
}

export function formatPackageFlightEntrySeatsHe(flightEntry) {
  const seats = resolvePackageFlightEntrySeats(flightEntry);
  if (seats == null) return null;
  return `${seats} מקומות`;
}

/**
 * @param {object|null} leg
 * @returns {{ routeLine: string, timeLine: string, metaLine: string, seatsLine: string|null } | null}
 */
export function legToPremiumSummary(leg) {
  if (!leg) return null;
  const [, depT] = splitDateTime(leg.depDate || "");
  const [, arrT] = splitDateTime(leg.arrDate || "");
  const depTime = (leg.depTime && String(leg.depTime).trim()) || depT || "—";
  const arrTime = (leg.arrTime && String(leg.arrTime).trim()) || arrT || "—";

  const depHe = formatPackageLegEndpointHe(leg, "dep");
  const arrHe = formatPackageLegEndpointHe(leg, "arr");
  const routeLine = `${depHe} → ${arrHe}`;
  const timeLine = `${depTime} — ${arrTime}`;

  const legCount = Number(leg.legCount ?? leg.numLegs);
  let isDirect = true;
  if (leg.isDirect === false) isDirect = false;
  else if (Number.isFinite(legCount) && legCount > 1) isDirect = false;

  const durMin = Number(leg.duration);
  let durHe = null;
  if (Number.isFinite(durMin) && durMin > 0) {
    const h = Math.floor(durMin / 60);
    const m = Math.round(durMin % 60);
    durHe = m > 0 ? `${h}ש ${m}ד` : `${h}ש`;
  }

  const metaParts = [];
  if (isDirect) metaParts.push("ישיר");
  if (durHe) metaParts.push(durHe);
  const metaLine = metaParts.join(" · ") || "—";
  const seatsLine = formatPackageLegSeatsHe(leg);

  return { routeLine, timeLine, metaLine, seatsLine };
}

export function legIsDirect(leg) {
  if (!leg) return false;
  if (leg.isDirect === false) return false;
  const legCount = Number(leg.legCount ?? leg.numLegs);
  if (Number.isFinite(legCount) && legCount > 1) return false;
  return true;
}

export function legsAreBothDirect(depLeg, retLeg) {
  return legIsDirect(depLeg) && legIsDirect(retLeg);
}

const IATA_RE = /^[A-Za-z]{3}$/;

/**
 * קוד IATA מרגל LDS — רק אם קיים בנתונים. לא ממציאים קוד.
 */
export function readPackageLegIata(leg, side) {
  if (!leg || typeof leg !== "object") return null;
  const airport = side === "dep" ? leg.depAirport : leg.arrAirport;
  const city = side === "dep" ? leg.depCity : leg.arrCity;
  const airportCode =
    airport && typeof airport === "object" ? airport.code : airport;
  for (const raw of [airportCode, city]) {
    const token = String(raw ?? "")
      .trim()
      .toUpperCase();
    if (IATA_RE.test(token)) return token;
  }
  return null;
}

/**
 * שורת טיסה לכרטיס שוק: TLV 18:15 → DXB 22:25 כשיש קודים; אחרת route + זמנים קיימים.
 */
export function compactMarketplaceFlightLeg(leg) {
  const summary = legToPremiumSummary(leg);
  if (!summary) return null;
  const depCode = readPackageLegIata(leg, "dep");
  const arrCode = readPackageLegIata(leg, "arr");
  const times = String(summary.timeLine || "").split(/\s+[—–-]\s+/);
  const depTime = (times[0] || "").trim();
  const arrTime = (times[1] || "").trim();
  const hasClock = depTime && arrTime && depTime !== "—" && arrTime !== "—";
  const compactLine =
    depCode && arrCode && hasClock
      ? `${depCode} ${depTime} → ${arrCode} ${arrTime}`
      : [summary.routeLine, hasClock ? `${depTime} → ${arrTime}` : summary.timeLine]
          .filter(Boolean)
          .join(" · ");
  return {
    ...summary,
    depCode,
    arrCode,
    depTime: hasClock ? depTime : null,
    arrTime: hasClock ? arrTime : null,
    compactLine,
    isDirect: legIsDirect(leg),
  };
}
